import React, { useState, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { usePagination } from "../hooks";
import {
  Box,
  Typography,
  IconButton,
  InputBase,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  Button,
  Skeleton,
  Divider,
} from "@mui/material";
import {
  Search as SearchIcon,
  RestartAlt as RestartAltIcon,
} from "@mui/icons-material";
import { translateText } from "../translations/translationService";

import { SCREEN_IDS } from "../components/FullComponent.js";
import { typeConnect } from "../constants/Constants";
import { getFullDocumentAllClient } from "../services/ConnectDataBase";
import MenuDocument from "../components/Info/ComponentMenuDocument.js";
import { USER_DOCUMENT_SCREEN_COLORS } from "../style/colors.js";

export default function UserDocumentScreen() {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [refSearch, setRefSearch] = useState("");
  const [searchResult, setSearchResult] = useState(null);
  const [searchOK, setSearchOk] = useState(false);
  const [openModalForClient, setOpenModalForClient] = useState(null);
  const lang = useSelector((state) => state.lang.value);
  const screenName = "UserDocumentScreen";
  const currentScreenId = SCREEN_IDS.DOCUMENT;

  // Hook de paginación
  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } = usePagination(0, 20);

  const getMo = useCallback(async () => {
    setIsLoading(true);
    try {
      const fullMo = await getFullDocumentAllClient(
        screenName,
        typeConnect[0],
        currentScreenId
      );

      setDocuments(fullMo.data);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.error("Error al obtener documentos:", error);
    }
  }, [screenName, typeConnect, currentScreenId]);

  useEffect(() => {
    getMo();
  }, [getMo]);

  useEffect(() => {
    handleSearch();
  }, [refSearch]);

  const handleSearch = () => {
    const trimmedRefSearch = refSearch.trim().toLowerCase();

    if (trimmedRefSearch === "") {
      setSearchOk(false);
      setSearchResult(null);
    } else {
      const searchTerms = trimmedRefSearch
        .split(" ")
        .map((term) => term.trim());

      const result = documents.filter((item) =>
        searchTerms.some((term) => {
          const clienteString = String(item.cliente);
          return (
            clienteString.toLowerCase().includes(term) ||
            (typeof item.nombre === "string" &&
              item.nombre.toLowerCase().includes(term))
          );
        })
      );

      setSearchOk(true);
      setSearchResult(result);
      handleChangePage(null, 0);
    }
  };

  const rowsToShow = searchOK ? searchResult : documents;

  const paginatedRows = rowsToShow.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );


  return (
    <Box sx={{ padding: 1.5 }}>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 1.5,
          padding: 1.5,
          backgroundColor: "white",
          borderRadius: 2,
          boxShadow:
            "0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)",
          border: `1px solid ${USER_DOCUMENT_SCREEN_COLORS.BORDER_LIGHT || "#e5e7eb"}`,
          marginBottom: 1.5,
        }}>
        <Box sx={{ flex: 1, minWidth: "280px", position: "relative" }}>
          <InputBase
            value={refSearch}
            onChange={(e) => setRefSearch(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && handleSearch()}
            placeholder={translateText("Buscar cliente o nombre", lang)}
            sx={{
              width: "100%",
              backgroundColor: USER_DOCUMENT_SCREEN_COLORS.BACKGROUND_CARD || "#F9FAFB",
              color: USER_DOCUMENT_SCREEN_COLORS.TEXT_PRIMARY || "#374151",
              fontSize: "0.85rem",
              fontWeight: 500,
              padding: "8px 12px 8px 40px",
              borderRadius: 2,
              border: `1.5px solid ${USER_DOCUMENT_SCREEN_COLORS.BORDER_CARD || "#e5e7eb"}`,
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: USER_DOCUMENT_SCREEN_COLORS.BACKGROUND_INPUT || "#F3F4F6",
                borderColor: USER_DOCUMENT_SCREEN_COLORS.BORDER_BRAND || "#174045",
                boxShadow: "0 2px 4px rgba(70, 93, 100, 0.1)",
              },
              "&.Mui-focused": {
                backgroundColor: "white",
                borderColor: USER_DOCUMENT_SCREEN_COLORS.BORDER_BRAND || "#174045",
                boxShadow:
                  "0 0 0 2px rgba(70, 93, 100, 0.1), 0 2px 4px rgba(70, 93, 100, 0.1)",
              },
              "& input": {
                padding: 0,
              },
              "& input::placeholder": {
                color: USER_DOCUMENT_SCREEN_COLORS.TEXT_MUTED || "#9CA3AF",
                opacity: 1,
              },
            }}
          />

          <Box
            sx={{
              position: "absolute",
              left: 16,
              top: "50%",
              transform: "translateY(-50%)",
              color: USER_DOCUMENT_SCREEN_COLORS.TEXT_MUTED || "#9CA3AF",
              pointerEvents: "none",
            }}>
            <SearchIcon sx={{ fontSize: 18 }} />
          </Box>
        </Box>

        <Button
          onClick={handleSearch}
          variant='contained'
          size="small"
          startIcon={<SearchIcon />}
          sx={{
            backgroundColor: USER_DOCUMENT_SCREEN_COLORS.BACKGROUND_BUTTON_PRIMARY || "#174045",
            color: "white",
            borderRadius: 2,
            fontSize: "0.8rem",
            fontWeight: 600,
            padding: "6px 16px",
            textTransform: "none",
            boxShadow: "none",
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: USER_DOCUMENT_SCREEN_COLORS.BACKGROUND_BUTTON_SECONDARY || "#1e4d52",
              transform: "translateY(-1px)",
              boxShadow: "0 4px 12px rgba(70, 93, 100, 0.2)",
            },
          }}>
          {translateText("Buscar", lang)}
        </Button>

        <Box
          sx={{
            display: { xs: "none", lg: "block" },
            width: "1px",
            height: 32,
            backgroundColor: USER_DOCUMENT_SCREEN_COLORS.BORDER_CARD || "#e5e7eb",
          }}
        />

        <Typography variant='h6' sx={{ flex: 1, textAlign: "center", fontSize: "1rem", fontWeight: 600 }}>
          {translateText("Tabla de Documentación", lang)}
        </Typography>

        <IconButton
          onClick={() => {
            setRefSearch("");
            handleSearch();
            getMo();
          }}
          sx={{
            padding: 1,
            color: USER_DOCUMENT_SCREEN_COLORS.TEXT_BRAND || "#174045",
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: "rgba(70, 93, 100, 0.08)",
              transform: "scale(1.1)",
            },
          }}>
          <RestartAltIcon />
        </IconButton>
      </Box>

      {isLoading ? (
        <Paper
          sx={{
            borderRadius: 2,
            overflow: "hidden",
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            border: `1px solid ${USER_DOCUMENT_SCREEN_COLORS.BORDER_LIGHT || "#e5e7eb"}`,
          }}>
          <Box sx={{ p: 2 }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {[
                      "#",
                      translateText("Cliente", lang),
                      translateText("Nombre", lang),
                      translateText("Acciones", lang),
                    ].map((header, i) => (
                      <TableCell key={i} sx={{ py: 1, px: 1.5 }}>
                        <Skeleton variant="text" width="100%" height={24} />
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[...Array(5)].map((_, i) => (
                    <TableRow key={i}>
                      {[...Array(4)].map((_, j) => (
                        <TableCell key={j} sx={{ py: 0.75, px: 1.5 }}>
                          <Skeleton variant="text" width="100%" height={20} />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Paper>
      ) : (
        <Paper
          sx={{
            borderRadius: 2,
            overflow: "hidden",
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            border: `1px solid ${USER_DOCUMENT_SCREEN_COLORS.BORDER_LIGHT || "#e5e7eb"}`,
          }}>
          <TableContainer sx={{ maxHeight: "calc(78vh - 60px)" }}>
            <Table stickyHeader size="small" sx={{ width: "100%" }}>
              <TableHead>
                <TableRow>
                  <TableCell
                    align='center'
                    sx={{
                      fontWeight: 600,
                      backgroundColor: USER_DOCUMENT_SCREEN_COLORS.BACKGROUND_TABLE_HEADER || "#F9FAFB",
                      borderBottom: `1px solid ${USER_DOCUMENT_SCREEN_COLORS.BORDER_TABLE || "#e5e7eb"}`,
                      fontSize: "0.75rem",
                      color: "black",
                      py: 1,
                      px: 1.5,
                      width: "60px",
                    }}>
                    #
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      backgroundColor: USER_DOCUMENT_SCREEN_COLORS.BACKGROUND_TABLE_HEADER || "#F9FAFB",
                      borderBottom: `1px solid ${USER_DOCUMENT_SCREEN_COLORS.BORDER_TABLE || "#e5e7eb"}`,
                      fontSize: "0.75rem",
                      color: "black",
                      py: 1,
                      px: 1.5,
                      width: "120px",
                    }}>
                    {translateText("Cliente", lang)}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      backgroundColor: USER_DOCUMENT_SCREEN_COLORS.BACKGROUND_TABLE_HEADER || "#F9FAFB",
                      borderBottom: `1px solid ${USER_DOCUMENT_SCREEN_COLORS.BORDER_TABLE || "#e5e7eb"}`,
                      fontSize: "0.75rem",
                      color: "black",
                      py: 1,
                      px: 1.5,
                      minWidth: "280px",
                    }}>
                    {translateText("Nombre", lang)}
                  </TableCell>
                  <TableCell
                    align='center'
                    sx={{
                      fontWeight: 600,
                      backgroundColor: USER_DOCUMENT_SCREEN_COLORS.BACKGROUND_TABLE_HEADER || "#F9FAFB",
                      borderBottom: `1px solid ${USER_DOCUMENT_SCREEN_COLORS.BORDER_TABLE || "#e5e7eb"}`,
                      fontSize: "0.75rem",
                      color: "black",
                      py: 1,
                      px: 1.5,
                      width: "100px",
                    }}>
                    {translateText("Acciones", lang)}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedRows.map((item, index) => (
                  <TableRow
                    key={`${item.cliente}-${index}`}
                    onClick={() => setOpenModalForClient(item.cliente)}
                    sx={{
                      "&:hover": {
                        backgroundColor: USER_DOCUMENT_SCREEN_COLORS.BACKGROUND_ROW_ALTERNATE || "#F9FAFB",
                        cursor: "pointer",
                      },
                      backgroundColor: index % 2 === 0 ? USER_DOCUMENT_SCREEN_COLORS.BACKGROUND_ROW_EVEN : USER_DOCUMENT_SCREEN_COLORS.BACKGROUND_ROW_ODD,
                      borderBottom: `1px solid ${USER_DOCUMENT_SCREEN_COLORS.BORDER_LIGHT || "#e5e7eb"}`,
                    }}>
                    <TableCell
                      component='th'
                      scope='row'
                      align='center'
                      sx={{
                        fontSize: "0.8rem",
                        py: 0.75,
                        px: 1.5,
                        color: USER_DOCUMENT_SCREEN_COLORS.TEXT_PRIMARY || "#374151",
                        fontWeight: 500,
                      }}>
                      {page * rowsPerPage + index + 1}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "0.8rem",
                        py: 0.75,
                        px: 1.5,
                        color: USER_DOCUMENT_SCREEN_COLORS.TEXT_SECONDARY || "#6B7280",
                        fontWeight: 600,
                      }}>
                      {item.cliente}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "0.8rem",
                        py: 0.75,
                        px: 1.5,
                        color: USER_DOCUMENT_SCREEN_COLORS.TEXT_PRIMARY || "#374151",
                        maxWidth: "280px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={item.nombre}>
                      {item.nombre}
                    </TableCell>
                    <TableCell
                      align='center'
                      sx={{
                        py: 0.75,
                        px: 1.5,
                      }}
                      onClick={(e) => e.stopPropagation()}>
                      <MenuDocument 
                        data={item} 
                        openModal={openModalForClient === item.cliente}
                        onModalStateChange={(isOpen) => {
                          if (!isOpen) {
                            setOpenModalForClient(null);
                          }
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Divider />

          {searchOK && searchResult.length === 0 ? (
            <Box
              sx={{
                padding: 3,
                textAlign: "center",
                backgroundColor: USER_DOCUMENT_SCREEN_COLORS.BACKGROUND_FOOTER || "#F9FAFB",
              }}>
              <Typography
                variant='h6'
                sx={{
                  color: USER_DOCUMENT_SCREEN_COLORS.TEXT_SECONDARY || "#6B7280",
                }}>
                {translateText(
                  "No se encontraron resultados para la búsqueda.",
                  lang
                )}
              </Typography>
            </Box>
          ) : !searchOK && documents.length === 0 ? (
            <Box
              sx={{
                padding: 3,
                textAlign: "center",
                backgroundColor: USER_DOCUMENT_SCREEN_COLORS.BACKGROUND_FOOTER || "#F9FAFB",
              }}>
              <Typography
                variant='h6'
                sx={{
                  color: USER_DOCUMENT_SCREEN_COLORS.TEXT_SECONDARY || "#6B7280",
                }}>
                {translateText("Todos los documentos han sido cargados.", lang)}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ backgroundColor: USER_DOCUMENT_SCREEN_COLORS.BACKGROUND_FOOTER || "#F9FAFB" }}>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component='div'
                count={rowsToShow.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{
                  "& .MuiTablePagination-toolbar": {
                    fontSize: "0.75rem",
                    color: USER_DOCUMENT_SCREEN_COLORS.TEXT_SECONDARY || "#6B7280",
                    minHeight: "52px",
                  },
                  "& .MuiTablePagination-select": {
                    fontSize: "0.75rem",
                  },
                  "& .MuiTablePagination-displayedRows": {
                    fontSize: "0.75rem",
                  },
                }}
              />
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
}
